import {numUtil} from '@/helper/num';
import {updateSizeStatus, updateSizePercent, updateValuePercent} from './calc';
import type {Book, SpotPair} from '@/types';
import {BookItem} from '@/types';

export function convertSpotNewOrderBook(
  tradePair,
  bidCache,
  bidPriceEps,
  bidCacheRaw,
  bidPriceEpsRaw,
  askCache,
  askPriceEps,
  askCacheRaw,
  askPriceEpsRaw,
  caches,
  percentType
): Book {
  const {
    symbol,
    pricePrecision,
    quoteQtyScale: priceScale,
    baseQtyPrecision: qtyPrecision,
    baseQtyScale: qtyScale,
    quoteQtyPrecision,
  } = tradePair as SpotPair;
  const BID = 'bid';
  const ASK = 'ask';
  const bids = bidPriceEps.map((priceEp, i) =>
    convertOrder(priceEp, bidCache.get(priceEp), i, BID, pricePrecision, priceScale, qtyPrecision, qtyScale)
  );
  const bidsRaw = bidPriceEpsRaw.map((priceEp, i) =>
    convertOrder(priceEp, bidCacheRaw.get(priceEp), i, BID, pricePrecision, priceScale, qtyPrecision, qtyScale)
  );
  const asks = askPriceEps.map((priceEp, i) =>
    convertOrder(priceEp, askCache.get(priceEp), i, ASK, pricePrecision, priceScale, qtyPrecision, qtyScale)
  );
  const asksRaw = askPriceEpsRaw.map((priceEp, i) =>
    convertOrder(priceEp, askCacheRaw.get(priceEp), i, ASK, pricePrecision, priceScale, qtyPrecision, qtyScale)
  );

  const now = Date.now();
  const {bidSizeCache, askSizeCache, bidTimeCache, askTimeCache, bidStatusCache, askStatusCache} = caches;
  updateSizeStatus(bids, now, bidSizeCache, bidTimeCache, bidStatusCache);
  updateSizeStatus(asks, now, askSizeCache, askTimeCache, askStatusCache);

  if (percentType === 'Stack') {
    updateTotalBySize(bids, asks, qtyPrecision, qtyScale);
  }
  if (percentType === 'Each') {
    updateTotalByValue(bids, asks, priceScale, qtyScale, quoteQtyPrecision);
  }

  return {
    reset: false,
    symbol,
    bids,
    asks,
    bid1Ep: bids[0]?.priceEp ?? 0,
    bid1EpRaw: bidsRaw[0]?.priceEp ?? 0,
    ask1Ep: asks[0]?.priceEp ?? 0,
    ask1EpRaw: asksRaw[0]?.priceEp ?? 0,
  };
}

function convertOrder(priceEp, size, index, side, pricePrecision, priceScale, qtyPrecision, qtyScale): BookItem {
  return {
    priceEp,
    price: numUtil.delimit(numUtil.floor(priceEp, pricePrecision, priceScale)),
    size,
    sizeStr: numUtil.abbreviateNumber(numUtil.floor(size, qtyPrecision, qtyScale), qtyPrecision),
    total: 0,
    totalStr: '0',
    percent: '0',
    [side]: true,
    odd: index % 2 !== 0,
    ss: 0,
    rowSize: size,
  };
}

function sizeTotalReducer(p, c, qtyPrecision, scale) {
  const total = p + c.size;
  c.total = total;
  c.totalStr = numUtil.abbreviateNumber(numUtil.floor(total, qtyPrecision, scale), qtyPrecision);
  return total;
}

function updateTotalBySize(bids, asks, qtyPrecision, scale) {
  const bidTotal = bids.reduce((p, c) => sizeTotalReducer(p, c, qtyPrecision, scale), 0);
  const askTotal = asks.reduce((p, c) => sizeTotalReducer(p, c, qtyPrecision, scale), 0);
  const total = Math.max(bidTotal, askTotal);
  updateSizePercent(bids, total);
  updateSizePercent(asks, total);
}

function valueTotalReducer(p, c, priceScale, qtyScale, quoteQtyPrecision) {
  const value = c.priceEp * c.size;
  c.total = value;
  const xx = numUtil.floor(value, quoteQtyPrecision, priceScale + qtyScale);
  c.totalStr = numUtil.delimit(xx);
  return Math.max(p, value);
}

function updateTotalByValue(bids, asks, priceScale, qtyScale, quoteQtyPrecision) {
  const bidTotal = bids.reduce((p, c) => valueTotalReducer(p, c, priceScale, qtyScale, quoteQtyPrecision), 0);
  const askTotal = asks.reduce((p, c) => valueTotalReducer(p, c, priceScale, qtyScale, quoteQtyPrecision), 0);
  const total = Math.max(bidTotal, askTotal);
  updateValuePercent(bids, total);
  updateValuePercent(asks, total);
}
